import { Accessibility, CheckCircle2, LockKeyhole, ShieldCheck, TimerReset, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import type { AttemptEvent, ChildProfile, TrustEvent } from "../../types";
import { minutesLabel } from "../../lib/progressionEngine";

interface TrustDashboardProps {
  attempts: AttemptEvent[];
  children: ChildProfile[];
  trustEvents: TrustEvent[];
}

export function TrustDashboard({ attempts, children, trustEvents }: TrustDashboardProps) {
  const averageMinutes = Math.round(children.reduce((sum, child) => sum + child.weeklyMinutes, 0) / children.length);
  const actionEvents = trustEvents.filter((event) => event.level !== "ok");

  return (
    <div className="trust-layout">
      <section className="surface-panel trust-overview" aria-labelledby="trust-title">
        <div className="panel-heading">
          <div>
            <h3 id="trust-title">Trust dashboard</h3>
            <span>Safety, accessibility, privacy, and bounded learning checks.</span>
          </div>
          <span className="healthy-badge">
            <ShieldCheck size={18} aria-hidden="true" />
            Everything looks good
          </span>
        </div>

        <div className="trust-score-grid">
          <TrustScore icon={<LockKeyhole size={18} />} label="Tracking" value="None" />
          <TrustScore icon={<TimerReset size={18} />} label="Avg. week" value={minutesLabel(averageMinutes)} />
          <TrustScore icon={<Accessibility size={18} />} label="Access tools" value="On" />
          <TrustScore icon={<CheckCircle2 size={18} />} label="Attempt logs" value={String(attempts.length)} />
        </div>

        <div className="trust-events">
          {trustEvents.map((event) => (
            <article className={`trust-event trust-${event.level}`} key={event.id}>
              <span className="mini-icon" aria-hidden="true">
                {event.level === "ok" ? <CheckCircle2 size={18} /> : <TriangleAlert size={18} />}
              </span>
              <div>
                <strong>{event.label}</strong>
                <p>{event.detail}</p>
              </div>
              <time>{event.time}</time>
            </article>
          ))}
        </div>
      </section>

      <aside className="surface-panel trust-settings" aria-labelledby="settings-title">
        <div className="panel-heading">
          <h3 id="settings-title">Safety settings</h3>
          <span>Learning app policy</span>
        </div>
        <SettingRow label="No child external links" value="Locked" />
        <SettingRow label="No open chat" value="Locked" />
        <SettingRow label="No behavioural ads" value="Locked" />
        <SettingRow label="Nesty quiet hours" value="Integration-ready" />
        <SettingRow label="Learning tile" value="Ready for dashboard" />
        <div className="trust-action">
          <strong>{actionEvents.length === 0 ? "No action needed" : `${actionEvents.length} item needs review`}</strong>
          <p>Parent-facing reports stay plain-English and do not publish unproven outcome claims.</p>
        </div>
      </aside>
    </div>
  );
}

function TrustScore({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <article className="trust-score">
      <span className="mini-icon" aria-hidden="true">{icon}</span>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <article className="setting-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}
